/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { REQUEST_NEGOTIABLE_QUOTE_MUTATION } from './graphql/RequestNegotiableQuoteMutation';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';

export interface RequestNegotiableQuoteInput {
  cartId: string;
  quoteName: string;
  comment: string;
  isDraft?: boolean;
  attachments?: { key: string }[];
}

export const requestNegotiableQuote = async (
  input: RequestNegotiableQuoteInput
): Promise<NegotiableQuoteModel | null> => {
  const { cartId, quoteName, comment, attachments, isDraft } = input;

  if (!cartId) {
    throw new Error('Cart ID is required');
  }

  if (!quoteName) {
    throw new Error('Quote name is required');
  }

  if (!comment) {
    throw new Error('Comment is required');
  }

  return fetchGraphQl(REQUEST_NEGOTIABLE_QUOTE_MUTATION, {
    variables: {
      cartId,
      quoteName,
      comment: attachments?.length ? { comment, attachments } : { comment },
      isDraft,
    },
  }).then((response) => {
    const { errors } = response;

    if (errors) {
      // Provide more detailed error information
      const errorMessages = errors.map((error) => error.message).join('; ');
      throw new Error(`Failed to request negotiable quote: ${errorMessages}`);
    }

    // Transform the quote data
    const quoteData = transformQuote(response.data?.requestNegotiableQuote?.quote);

    if (!quoteData) {
      throw new Error(
        'Failed to transform quote data: Invalid response structure'
      );
    }

    // Emit event with transformed  quote data on success
    events.emit('quote-management/negotiable-quote-requested', {
      quote: quoteData,
      input: { cartId, quoteName, comment, attachments, isDraft },
    });

    return quoteData;
  });
};
